import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { useOptionalProgress } from '../state/ProgressContext';
import { useAuth } from '../state/AuthContext';
import { StatusBadge } from '../components/StatusBadge';
import { routeForFurthestProgress } from '../lib/progressRoutes';
import { hvacQuestionSections, type HvacQuestion, type WorkMode } from '../content/hvac';

type Answers = Record<string, string | boolean>;
type ResumeInfo = { fileName: string; sizeBytes: number; uploadedAt: string; textStatus: string };

const RESUME_ACCEPT = '.pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain';
const RESUME_MAX_BYTES = 10 * 1024 * 1024;

/** The resume upload box. The file is stored as soon as it is chosen; a new file replaces it. */
function ResumeUpload({ resume, disabled, onUploaded }: { resume: ResumeInfo | null; disabled: boolean; onUploaded: (resume: ResumeInfo) => void }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function upload(file: File | undefined) {
    if (!file) return;
    setError(null);
    if (file.size > RESUME_MAX_BYTES) { setError('That file is too large. The limit is 10 MB.'); return; }
    if (!/\.(pdf|docx|txt)$/i.test(file.name)) { setError('Upload your resume as a PDF, Word (.docx) or text file.'); return; }
    setUploading(true);
    try {
      const result = await api.upload<{ resume: ResumeInfo }>('/api/application/resume', file);
      onUploaded(result.resume);
    } catch (uploadError) {
      setError(uploadError instanceof ApiError ? uploadError.message : 'Could not upload your resume. Please try again.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return <div className="form-field card">
    <label htmlFor="hvac-resume"><strong>Resume</strong></label>
    <p className="field-help">Upload your resume as a PDF, Word (.docx) or text file, up to 10 MB. Any work history counts - no sales experience is needed.</p>
    {resume && <p>Uploaded: <strong>{resume.fileName}</strong> · {Math.max(1, Math.round(resume.sizeBytes / 1024))} KB</p>}
    <input
      ref={inputRef}
      id="hvac-resume"
      type="file"
      accept={RESUME_ACCEPT}
      disabled={disabled || uploading}
      onChange={(event) => void upload(event.target.files?.[0])}
    />
    {uploading && <p className="field-help">Uploading…</p>}
    {resume && !uploading && !disabled && <p className="field-help">Choose another file to replace it.</p>}
    {error && <div className="alert error" role="alert">{error}</div>}
  </div>;
}

// Every answer is kept as the applicant typed it; yes/no questions are 'yes' | 'no' | '' in the
// form and become booleans only when submitted, the confirmations are booleans throughout.
function toPayload(answers: Answers, questions: HvacQuestion[]) {
  return Object.fromEntries(questions.map((question) => {
    const value = answers[question.key];
    if (question.type === 'yesno') return [question.key, value === 'yes' || value === true];
    if (question.type === 'confirm') return [question.key, value === true];
    return [question.key, typeof value === 'string' ? value : ''];
  }));
}

function toFormValue(question: HvacQuestion, value: unknown): string | boolean {
  if (question.type === 'confirm') return value === true;
  if (question.type === 'yesno') return value === true ? 'yes' : value === false ? '' : typeof value === 'string' ? value : '';
  return typeof value === 'string' ? value : '';
}

function answered(question: HvacQuestion, value: string | boolean | undefined) {
  if (question.type === 'confirm') return value === true;
  if (question.type === 'yesno') return value === 'yes' || value === 'no';
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * The HVAC Speed-to-Lead application. Short, plain questions about how the applicant would do the
 * job - no experience questions and no mock-call recording. `preview` renders it for an admin
 * without loading or saving anything.
 */
export function HvacApplicationQuestions({ workMode, preview = false }: { workMode: WorkMode; preview?: boolean }) {
  const sections = hvacQuestionSections(workMode);
  const questions = sections.flatMap((section) => section.questions);
  const [answers, setAnswers] = useState<Answers>({});
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resume, setResume] = useState<ResumeInfo | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const navigate = useNavigate();
  const progressState = useOptionalProgress();
  const progress = progressState?.progress ?? null;
  const { user } = useAuth();
  const canFillTemplate = Boolean(user?.isTester) || Boolean(user?.isAdmin);
  const locked = !preview && Boolean(progress?.flags.applicationQuestionsDone);
  const inputsDisabled = locked || Boolean(loadError);

  const load = useCallback(() => {
    setLoadError(null);
    return api.get<{ application: Record<string, unknown> }>('/api/application')
      .then(({ application }) => setAnswers(Object.fromEntries(questions.map((question) => [question.key, toFormValue(question, application[question.key])]))))
      .catch((loadFailure) => setLoadError(loadFailure instanceof ApiError ? loadFailure.message : 'Could not load your application.'))
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workMode]);

  useEffect(() => {
    if (preview) { setLoaded(true); return; }
    void load();
    api.get<{ resume: ResumeInfo | null }>('/api/application/resume').then((result) => setResume(result.resume)).catch(() => undefined);
  }, [preview, load]);

  function change(key: string, value: string | boolean) {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (timer.current) clearTimeout(timer.current);
    if (preview || inputsDisabled) return;
    timer.current = setTimeout(() => { void api.put('/api/application/draft', draftPayload(next)).catch(() => undefined); }, 700);
  }

  // A draft keeps unanswered yes/no questions out rather than saving them as "no".
  function draftPayload(values: Answers) {
    const payload = toPayload(values, questions);
    for (const question of questions) if (question.type === 'yesno' && !answered(question, values[question.key])) delete payload[question.key];
    return payload;
  }

  function fillTemplate() {
    const template: Answers = {
      fullName: 'Test Applicant', isAtLeast18: 'yes', timeZone: 'Eastern', earliestStartDate: new Date().toISOString().slice(0, 10),
      weeklyAvailability: 'About 20 hours a week, weekday mornings and early afternoons.',
      hasPhoneAndQuietPlace: 'yes', hasReliableTransportation: 'yes',
      whyCommissionFits: 'I want pay that depends on the work I put in, and I can put in steady hours right now while I look for something long term.',
      fiveNosInARow: 'Keep going to the next business. I would also look at the feedback from my admin to see if I am saying something that turns people off and try a different opening.',
      productQuestionUnknown: 'Tell them honestly I am not sure, write the question down, ask my sales manager, and get back to them with the right answer.',
      pitchConfusingFeedback: 'Ask which part is confusing, cut it or say it more simply, and try the new version on my next calls.',
      recordingEveryLead: 'I will fill in the result right after each business before I move to the next one, so it never piles up.',
      keptGoingWhenFrustrating: 'When I was learning to drive stick I stalled constantly for two weeks. I kept practicing every evening in a parking lot until I got it.',
      firstCallsNoAnswerScenario: 'Keep calling down the list, note which ones did not answer so I can try them again at a different time of day.',
      ownerBusyScenario: 'Ask when is a better time to catch the owner, leave my name, write down the time, and come back then.',
      confirmsCallingStrangers: true, confirmsRejectionAndNoAnswer: true, confirmsVisitingInPerson: true, confirmsTalkingToStrangers: true, confirmsBusyOrUninterested: true,
      confirmsIndependentContractor: true, confirmsCommissionOnly: true, confirmsNoGuaranteedPay: true, confirmsPaidOnlyOnPaidCustomers: true,
      confirmsNoRecurringCommission: true, confirmsRecordEveryLead: true, confirmsOnlyApprovedStatements: true,
    };
    const next = Object.fromEntries(questions.map((question) => [question.key, template[question.key] ?? ''])) as Answers;
    setAnswers(next);
    if (!preview) void api.put('/api/application/draft', draftPayload(next)).catch(() => undefined);
  }

  const complete = questions.every((question) => answered(question, answers[question.key])) && (preview || Boolean(resume));

  async function submit() {
    if (submitting || locked || preview) return;
    setError(null);
    setSubmitting(true);
    try {
      if (timer.current) clearTimeout(timer.current);
      await api.post('/api/application/submit', toPayload(answers, questions));
      await progressState?.refresh();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (submitError) { setError(submitError instanceof ApiError ? submitError.message : 'Could not submit your application.'); }
    finally { setSubmitting(false); }
  }

  if (!loaded) return <div className="loading-state">Loading your application…</div>;

  function renderQuestion(question: HvacQuestion) {
    const value = answers[question.key];
    const id = `hvac-${question.key}`;
    if (question.type === 'confirm') {
      return <div className="checkbox-row" key={question.key}>
        <input id={id} type="checkbox" checked={value === true} disabled={inputsDisabled} onChange={(event) => change(question.key, event.target.checked)} />
        <label htmlFor={id}>{question.label}</label>
      </div>;
    }
    return <div className="form-field card" key={question.key}>
      <label htmlFor={id}><strong>{question.label}</strong></label>
      {question.type === 'yesno' ? (
        <select id={id} value={typeof value === 'string' ? value : ''} disabled={inputsDisabled} onChange={(event) => change(question.key, event.target.value)} required>
          <option value="">Select an answer</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      ) : question.type === 'textarea' ? (
        <textarea id={id} rows={4} value={typeof value === 'string' ? value : ''} disabled={inputsDisabled} maxLength={4000} onChange={(event) => change(question.key, event.target.value)} required />
      ) : (
        <input id={id} type={question.type === 'date' ? 'date' : 'text'} value={typeof value === 'string' ? value : ''} disabled={inputsDisabled} onChange={(event) => change(question.key, event.target.value)} required />
      )}
      {question.help && <p className="field-help">{question.help}</p>}
    </div>;
  }

  return <div>
    <h1 className="step-title">Application</h1>
    <p className="step-subtitle">Every question is required. No experience is needed - we want to know how you would do the job.</p>
    {preview && <div className="alert info"><strong>Preview</strong> — this is the live HVAC Speed-to-Lead {workMode === 'FIELD' ? 'Field' : 'Remote'} application form. Nothing here is saved or submitted.</div>}
    {canFillTemplate && !inputsDisabled && <button type="button" className="btn-secondary" style={{ marginBottom: 16 }} onClick={fillTemplate}>Fill with passing template (test)</button>}
    {loadError && <div className="alert error" role="alert">
      <p>{loadError}</p>
      <p>Your saved answers could not be loaded, so the form is read-only to avoid overwriting them.</p>
      <button type="button" className="btn-secondary" onClick={() => void load()}>Try again</button>
    </div>}
    {locked && <div className={`alert ${progress?.flags.applicationApproved ? 'success' : 'info'} application-submitted-box`}>
      <p><strong>{progress?.flags.applicationApproved ? 'Approved' : 'Submitted'}</strong> <StatusBadge status={progress?.application.status ?? 'PENDING'} /></p>
      {progress?.application.submittedAt && <p>Submitted {new Date(progress.application.submittedAt).toLocaleString()}.</p>}
      {progress?.application.userVisibleExplanation && <p>{progress.application.userVisibleExplanation}</p>}
      {progress?.flags.applicationApproved
        ? <p><button className="btn-primary" type="button" onClick={() => navigate(routeForFurthestProgress(progress))}>Continue</button></p>
        : progress?.application.status === 'PENDING' && <p>A person at Krovance reads every application. You will get an email as soon as a decision is made.</p>}
    </div>}
    {sections.map((section) => <section key={section.title} style={{ marginBottom: 20 }}>
      <h2 style={{ fontSize: '1.1rem', margin: '18px 0 6px' }}>{section.title}</h2>
      {section.intro && <p className="field-help">{section.intro}</p>}
      {section.questions.every((question) => question.type === 'confirm')
        ? <div className="card hvac-confirm-list">{section.questions.map(renderQuestion)}</div>
        : section.questions.map(renderQuestion)}
    </section>)}
    <section style={{ marginBottom: 20 }}>
      <h2 style={{ fontSize: '1.1rem', margin: '18px 0 6px' }}>Your resume</h2>
      <ResumeUpload resume={resume} disabled={preview || inputsDisabled} onUploaded={setResume} />
    </section>
    {error && <div className="alert error">{error}</div>}
    {!locked && !preview && <button className="btn-primary" disabled={submitting || !complete} onClick={() => void submit()}>{submitting ? 'Submitting…' : 'Submit Application'}</button>}
    {!locked && !preview && !complete && <p className="field-help">{resume ? 'Answer every question and tick every box to submit.' : 'Answer every question, tick every box and upload your resume to submit.'}</p>}
    {preview && <p className="field-help">{complete ? 'Every required answer is filled in.' : 'Some required answers are still empty.'}</p>}
  </div>;
}
